import { useEffect, useState } from 'react'

export type DaySchedule = {
  open: string // 'HH:mm' 24h format
  close: string // 'HH:mm' 24h format
} | null

export type RestaurantSchedule = {
  [dayIndex: number]: DaySchedule // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
}

// ─── TEMPORARY ORDERING OVERRIDES (Costa Rica timezone) ──────────────
// Use this map ONLY to grant a restaurant a short-lived "force open"
// window OUTSIDE its normal schedule. After the expiry timestamp passes,
// the override is ignored automatically and the regular schedule kicks
// back in. No rollback needed.
//
// Format: slug → ISO 8601 timestamp WITH timezone offset.
// Leave the map empty (or remove the entry) once it's no longer needed.
export const TEMPORARY_OPEN_OVERRIDES: Record<string, string> = {
  // Add entries here (slug → ISO timestamp) to grant a short-lived
  // "force open" window outside normal hours. Auto-expires — no rollback.
  //
  // NatuCafé — abierto temporalmente para una orden de prueba MS Express.
  // Expira a las 9:55 PM (Costa Rica, UTC-6) del 9-jun-2026 (~1h desde las
  // 8:48 PM). Si tu hora actual + 1h es distinta, ajustá SOLO este timestamp.
  'natucafe-coffee-shop': '2026-06-09T21:55:00-06:00',
}

/** Returns true if a temporary override is active for the slug right now. */
function isTemporarilyForcedOpen(slug: string | undefined | null): boolean {
  if (!slug) return false
  const expiryIso = TEMPORARY_OPEN_OVERRIDES[slug]
  if (!expiryIso) return false
  const expiryTs = Date.parse(expiryIso)
  if (Number.isNaN(expiryTs)) return false
  return Date.now() < expiryTs
}

// Hardcoded schedules for restaurants (Costa Rica timezone)
// Day index: 0 = Sunday, 1 = Monday, ..., 6 = Saturday
// null = closed all day
export const RESTAURANT_SCHEDULES: Record<string, RestaurantSchedule> = {
  // Capricho de Canela — always open
  'capricho-de-canela': {
    0: { open: '00:00', close: '23:59' },
    1: { open: '00:00', close: '23:59' },
    2: { open: '00:00', close: '23:59' },
    3: { open: '00:00', close: '23:59' },
    4: { open: '00:00', close: '23:59' },
    5: { open: '00:00', close: '23:59' },
    6: { open: '00:00', close: '23:59' },
  },
  // Heladería Congelados
  'heladeria-congelados': {
    0: { open: '13:00', close: '18:00' }, // Sunday
    1: { open: '10:00', close: '18:00' },
    2: { open: '10:00', close: '18:00' },
    3: { open: '10:00', close: '18:00' },
    4: { open: '10:00', close: '17:00' },
    5: { open: '10:00', close: '18:00' },
    6: { open: '10:00', close: '18:00' },
  },
  // Crack'pollö — Lun a Sáb 10:00 – 22:00, Domingo cerrado
  'crack-pollo': {
    0: null, // Sunday closed
    1: { open: '10:00', close: '22:00' },
    2: { open: '10:00', close: '22:00' },
    3: { open: '10:00', close: '22:00' },
    4: { open: '10:00', close: '22:00' },
    5: { open: '10:00', close: '22:00' },
    6: { open: '10:00', close: '22:00' },
  },
  // NatuCafé Coffee Shop — Lun a Mié 11:00 – 19:00, Jue a Dom 11:00 – 20:00
  'natucafe-coffee-shop': {
    0: { open: '11:00', close: '20:00' }, // Sunday
    1: { open: '11:00', close: '19:00' },
    2: { open: '11:00', close: '19:00' },
    3: { open: '11:00', close: '19:00' },
    4: { open: '11:00', close: '20:00' },
    5: { open: '11:00', close: '20:00' },
    6: { open: '11:00', close: '20:00' },
  },
  // La Cabaña Parrillera — Lun a Mié cerrado, Jue/Vie 17-21, Sáb 12-21, Dom 12-19
  'cabana-parrillera': {
    0: { open: '12:00', close: '19:00' }, // Sunday
    1: null,
    2: null,
    3: null,
    4: { open: '17:00', close: '21:00' },
    5: { open: '17:00', close: '21:00' },
    6: { open: '12:00', close: '21:00' },
  },
  // Alias slug for La Cabaña Parrillera
  'la-cabana-parrillera': {
    0: { open: '12:00', close: '19:00' },
    1: null,
    2: null,
    3: null,
    4: { open: '17:00', close: '21:00' },
    5: { open: '17:00', close: '21:00' },
    6: { open: '12:00', close: '21:00' },
  },
  // Rancho San Francisco (restaurant id 28) — Lun a Mié cerrado,
  // Jue a Dom de 10:00 a 22:00
  'rancho-san-francisco': {
    0: { open: '10:00', close: '22:00' }, // Sunday
    1: null,
    2: null,
    3: null,
    4: { open: '10:00', close: '22:00' },
    5: { open: '10:00', close: '22:00' },
    6: { open: '10:00', close: '22:00' },
  },
  // Emilius Pizza (restaurant id 27) — Lun a Vie 16:00–21:30,
  // Sáb y Dom 11:00–21:30.
  'emilius-pizza': {
    0: { open: '11:00', close: '21:30' }, // Sunday
    1: { open: '16:00', close: '21:30' },
    2: { open: '16:00', close: '21:30' },
    3: { open: '16:00', close: '21:30' },
    4: { open: '16:00', close: '21:30' },
    5: { open: '16:00', close: '21:30' },
    6: { open: '11:00', close: '21:30' },
  },
}

export type ScheduleStatus = {
  isOpen: boolean
  hasSchedule: boolean
}

export function isRestaurantOpenBySchedule(
  slug: string | undefined,
): ScheduleStatus {
  if (!slug || !RESTAURANT_SCHEDULES[slug]) {
    return { isOpen: false, hasSchedule: false }
  }

  // Short-lived "force open" override — bypass the normal schedule for the
  // duration declared in TEMPORARY_OPEN_OVERRIDES. Auto-expires.
  if (isTemporarilyForcedOpen(slug)) {
    return { isOpen: true, hasSchedule: true }
  }

  const schedule = RESTAURANT_SCHEDULES[slug]

  // Get current time in Costa Rica timezone
  const now = new Date()
  const crTimeStr = now.toLocaleString('en-US', {
    timeZone: 'America/Costa_Rica',
  })
  const crDate = new Date(crTimeStr)

  const currentDay = crDate.getDay() // 0 = Sunday, 1 = Monday, etc.
  const currentHour = crDate.getHours()
  const currentMinute = crDate.getMinutes()

  const currentTimeNum = currentHour * 60 + currentMinute
  const todaySchedule = schedule[currentDay]

  if (!todaySchedule) {
    return { isOpen: false, hasSchedule: true }
  }

  const [openHour, openMinute] = todaySchedule.open.split(':').map(Number)
  const [closeHour, closeMinute] = todaySchedule.close.split(':').map(Number)

  const openTimeNum = openHour * 60 + openMinute
  const closeTimeNum = closeHour * 60 + closeMinute

  const isOpen = currentTimeNum >= openTimeNum && currentTimeNum < closeTimeNum

  return { isOpen, hasSchedule: true }
}

/**
 * Determines whether ordering should be BLOCKED based on the restaurant's
 * schedule. Returns `true` ONLY when the restaurant has an explicit schedule
 * entry AND is currently outside operating hours.
 *
 * Restaurants WITHOUT a schedule entry are treated as "always allowed" — this
 * is a safety fallback so we never accidentally block ordering for a restaurant
 * that hasn't been configured yet.
 */
export function isRestaurantClosedForOrdering(
  slug: string | undefined | null,
): boolean {
  if (!slug) return false
  const status = isRestaurantOpenBySchedule(slug)
  if (!status.hasSchedule) return false
  return !status.isOpen
}

/**
 * Returns a human-readable "next opening" hint for the given slug, or null.
 * Looks ahead up to 7 days to find the next day with an open window.
 */
export function getNextOpeningHint(
  slug: string | undefined | null,
): string | null {
  if (!slug || !RESTAURANT_SCHEDULES[slug]) return null
  const schedule = RESTAURANT_SCHEDULES[slug]

  const now = new Date()
  const crTimeStr = now.toLocaleString('en-US', {
    timeZone: 'America/Costa_Rica',
  })
  const crDate = new Date(crTimeStr)
  const currentDay = crDate.getDay()
  const currentHour = crDate.getHours()
  const currentMinute = crDate.getMinutes()
  const currentTimeNum = currentHour * 60 + currentMinute

  const DAY_NAMES_ES = [
    'domingo',
    'lunes',
    'martes',
    'miércoles',
    'jueves',
    'viernes',
    'sábado',
  ]

  // Check today (later in the day)
  const today = schedule[currentDay]
  if (today) {
    const [oh, om] = today.open.split(':').map(Number)
    const openNum = oh * 60 + om
    if (currentTimeNum < openNum) {
      return `Abrimos hoy a las ${formatTime(today.open)}`
    }
  }

  // Look ahead 1..7 days
  for (let offset = 1; offset <= 7; offset++) {
    const dayIdx = (currentDay + offset) % 7
    const day = schedule[dayIdx]
    if (day) {
      const label = offset === 1 ? 'mañana' : DAY_NAMES_ES[dayIdx]
      return `Abrimos ${label} a las ${formatTime(day.open)}`
    }
  }

  return null
}

function formatTime(hhmm: string): string {
  const [h, m] = hhmm.split(':').map(Number)
  const period = h >= 12 ? 'PM' : 'AM'
  const hour12 = h % 12 === 0 ? 12 : h % 12
  if (m === 0) return `${hour12}:00 ${period}`
  return `${hour12}:${String(m).padStart(2, '0')} ${period}`
}

/**
 * React hook that returns the current schedule status for a slug and
 * re-evaluates every minute so the UI flips automatically when the clock
 * crosses an open/close boundary.
 */
export function useRestaurantScheduleStatus(slug: string | undefined | null): {
  isOpen: boolean
  hasSchedule: boolean
  isClosedForOrdering: boolean
  nextOpeningHint: string | null
} {
  const compute = () => {
    const status = isRestaurantOpenBySchedule(slug ?? undefined)
    return {
      isOpen: status.isOpen,
      hasSchedule: status.hasSchedule,
      isClosedForOrdering: status.hasSchedule && !status.isOpen,
      nextOpeningHint:
        status.hasSchedule && !status.isOpen ? getNextOpeningHint(slug) : null,
    }
  }

  const [state, setState] = useState(compute)

  useEffect(() => {
    setState(compute())
    const interval = setInterval(() => {
      setState(compute())
    }, 30_000) // re-check every 30s — cheap and snappy enough
    return () => clearInterval(interval)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug])

  return state
}
